import { useState, useEffect, useLayoutEffect, useRef, Fragment, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useSnapshot } from 'valtio'
import { packetsReplayState } from '../../state/packetsReplayState'
import { formatCount, formatDate } from '../../../formatUnits'
import { isMobileLayout } from '../../useIsMobile'
import styles from './TelemetryPanel.module.css'
import PlayerAvatar from './PlayerAvatar'
import CollapseTab from './CollapseTab'
import TelemetrySettings, { type TelemetryFieldId } from './TelemetrySettings'
import ChunksLoadingCard, { useChunksProgress } from './ChunksLoadingCard'

const TelemetryIcon = ({ className }: { className?: string }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className={className} aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.6666 9.16667C16.4627 7.70029 15.7824 6.34161 14.7305 5.29988C13.6785 4.25816 12.3132 3.59117 10.8449 3.40167C9.5337 3.23158 8.20141 3.45034 7.01342 4.03078C5.82544 4.61121 4.83408 5.52777 4.16242 6.66667M3.33325 10.8333C3.52604 12.2209 4.14579 13.5142 5.10656 14.5337C6.06734 15.5533 7.32153 16.2486 8.69525 16.5234C10.069 16.7981 11.4941 16.6386 12.7731 16.067C14.0521 15.4954 15.1216 14.54 15.8333 13.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14.9999 13.3333C14.9999 13.5544 15.0877 13.7663 15.244 13.9226C15.4003 14.0789 15.6122 14.1667 15.8333 14.1667C16.0543 14.1667 16.2662 14.0789 16.4225 13.9226C16.5788 13.7663 16.6666 13.5544 16.6666 13.3333C16.6666 13.1123 16.5788 12.9004 16.4225 12.7441C16.2662 12.5878 16.0543 12.5 15.8333 12.5C15.6122 12.5 15.4003 12.5878 15.244 12.7441C15.0877 12.9004 14.9999 13.1123 14.9999 13.3333ZM3.33325 6.66668C3.33325 6.88769 3.42105 7.09965 3.57733 7.25593C3.73361 7.41221 3.94557 7.50001 4.16659 7.50001C4.3876 7.50001 4.59956 7.41221 4.75584 7.25593C4.91212 7.09965 4.99992 6.88769 4.99992 6.66668C4.99992 6.44566 4.91212 6.2337 4.75584 6.07742C4.59956 5.92114 4.3876 5.83334 4.16659 5.83334C3.94557 5.83334 3.73361 5.92114 3.57733 6.07742C3.42105 6.2337 3.33325 6.44566 3.33325 6.66668ZM7.49992 10C7.49992 10.6631 7.76331 11.2989 8.23215 11.7678C8.70099 12.2366 9.33688 12.5 9.99992 12.5C10.663 12.5 11.2988 12.2366 11.7677 11.7678C12.2365 11.2989 12.4999 10.6631 12.4999 10C12.4999 9.33697 12.2365 8.70108 11.7677 8.23224C11.2988 7.7634 10.663 7.50001 9.99992 7.50001C9.33688 7.50001 8.70099 7.7634 8.23215 8.23224C7.76331 8.70108 7.49992 9.33697 7.49992 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const SlidersIcon = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={className} aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.25 8.68036C5.91289 8.68036 6.54878 8.9441 7.01758 9.41278C7.30457 9.69977 7.51311 10.0502 7.63281 10.4304H13.5C13.9141 10.4304 14.2498 10.7663 14.25 11.1804C14.25 11.5946 13.9142 11.9304 13.5 11.9304H7.63379C7.51409 12.3109 7.30488 12.6616 7.01758 12.9489C6.54878 13.4176 5.91289 13.6804 5.25 13.6804C4.58711 13.6804 3.95122 13.4176 3.48242 12.9489C3.19512 12.6616 2.98591 12.3109 2.86621 11.9304H2.5C2.0858 11.9304 1.75 11.5946 1.75 11.1804C1.75023 10.7663 2.08592 10.4304 2.5 10.4304H2.86719C2.98689 10.0502 3.19543 9.69977 3.48242 9.41278C3.95122 8.9441 4.58711 8.68036 5.25 8.68036ZM5.25 10.1804C4.9849 10.1804 4.73047 10.2859 4.54297 10.4733C4.3555 10.6608 4.25011 10.9153 4.25 11.1804C4.25 11.4454 4.35558 11.6999 4.54297 11.8874C4.73047 12.0749 4.9848 12.1804 5.25 12.1804C5.5152 12.1804 5.76953 12.0749 5.95703 11.8874C6.14442 11.6999 6.25 11.4454 6.25 11.1804C6.24989 10.9153 6.1445 10.6608 5.95703 10.4733C5.76953 10.2859 5.5151 10.1804 5.25 10.1804ZM10.75 2.32001C11.413 2.32001 12.0487 2.58363 12.5176 3.05243C12.8048 3.33963 13.0141 3.68961 13.1338 4.07001H13.5C13.9142 4.07001 14.25 4.40581 14.25 4.82001C14.25 5.23421 13.9142 5.57001 13.5 5.57001H13.1338C13.0141 5.95041 12.8048 6.30039 12.5176 6.58759C12.0487 7.05639 11.413 7.32001 10.75 7.32001C10.087 7.32001 9.45132 7.05639 8.98242 6.58759C8.69522 6.30039 8.48591 5.95041 8.36621 5.57001H2.5C2.0858 5.57001 1.75 5.23421 1.75 4.82001C1.75 4.40581 2.0858 4.07001 2.5 4.07001H8.36621C8.48591 3.68961 8.69522 3.33963 8.98242 3.05243C9.45132 2.58363 10.087 2.32001 10.75 2.32001ZM10.75 3.82001C10.4848 3.82001 10.2305 3.92538 10.043 4.11298C9.85537 4.30048 9.75 4.55481 9.75 4.82001C9.75 5.08521 9.85537 5.33954 10.043 5.52704C10.2305 5.71464 10.4848 5.82001 10.75 5.82001C11.0152 5.82001 11.2695 5.71464 11.457 5.52704C11.6446 5.33954 11.75 5.08521 11.75 4.82001C11.75 4.55481 11.6446 4.30048 11.457 4.11298C11.2695 3.92538 11.0152 3.82001 10.75 3.82001Z" fill="currentColor" />
  </svg>
)

/* Figma `16 / Decoration / light-bulb` — the Union vector exported from the
   badge instance, inset 1/2 px inside the 16px box like in the mockup. */
const LightBulbIcon = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={className} aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path transform="translate(1 2)" fill="currentColor" d="M6.75 2.66699C8.82107 2.66699 10.5 4.34592 10.5 6.41699C10.4999 7.71294 9.84107 8.85241 8.84375 9.52539V10.75C8.84375 11.7165 8.06025 12.5 7.09375 12.5H6.40625C5.43975 12.5 4.65625 11.7165 4.65625 10.75V9.52539C3.65893 8.85241 3.00011 7.71294 3 6.41699C3 4.34592 4.67893 2.66699 6.75 2.66699ZM6.15625 10.75C6.15625 10.8881 6.26818 11 6.40625 11H7.09375C7.23182 11 7.34375 10.8881 7.34375 10.75V9.84961H6.15625V10.75ZM6.75 4.16699C5.50736 4.16699 4.5 5.17435 4.5 6.41699C4.50012 7.23724 4.9391 7.95605 5.59766 8.34961H7.90234C8.5609 7.95605 8.99988 7.23724 9 6.41699C9 5.17435 7.99264 4.16699 6.75 4.16699ZM1.41699 6C1.83106 6.00018 2.16699 6.33589 2.16699 6.75C2.16699 7.16411 1.83106 7.49982 1.41699 7.5H0.75C0.335786 7.5 0 7.16421 0 6.75C0 6.33579 0.335786 6 0.75 6H1.41699ZM12.75 6C13.1642 6 13.5 6.33579 13.5 6.75C13.5 7.16421 13.1642 7.5 12.75 7.5H12.083C11.6689 7.49982 11.333 7.1641 11.333 6.75C11.333 6.3359 11.6689 6.00018 12.083 6H12.75ZM1.95312 1.95312C2.24602 1.66023 2.72078 1.66023 3.01367 1.95312L3.48047 2.41992C3.77317 2.71283 3.7733 3.18764 3.48047 3.48047C3.18764 3.7733 2.71283 3.77317 2.41992 3.48047L1.95312 3.01367C1.66023 2.72078 1.66023 2.24602 1.95312 1.95312ZM10.4863 1.95312C10.7792 1.66023 11.254 1.66023 11.5469 1.95312C11.8398 2.24602 11.8398 2.72078 11.5469 3.01367L11.0801 3.48047C10.7872 3.77317 10.3124 3.7733 10.0195 3.48047C9.7267 3.18764 9.72683 2.71283 10.0195 2.41992L10.4863 1.95312ZM6.75 0C7.16421 0 7.5 0.335786 7.5 0.75V1.41699C7.49982 1.83106 7.16411 2.16699 6.75 2.16699C6.33589 2.16699 6.00018 1.83106 6 1.41699V0.75C6 0.335786 6.33579 0 6.75 0Z" />
  </svg>
)

interface NearbyPlayer { name: string; distance: number }

interface TelemetryData {
  time: string
  date: string
  tps: number | null
  ping: number | null
  x: number; y: number; z: number
  yaw: number; pitch: number
  chunkX: number; chunkZ: number
  gameMode: string | null
  biome: string | null
  lightLevel: number | null
  motionXZ: number; motionY: number
  /** "walk" | "run" | "fly" | "fall" | "jump" | "idle" */
  motionState: string
  nearbyPlayers: NearbyPlayer[]
}

const EMPTY: TelemetryData = {
  time: '–:–:–', date: '',
  tps: null, ping: null,
  x: 0, y: 0, z: 0, yaw: 0, pitch: 0, chunkX: 0, chunkZ: 0,
  gameMode: null, biome: null, lightLevel: null,
  motionXZ: 0, motionY: 0, motionState: 'idle', nearbyPlayers: [],
}

const GAME_MODE_LABEL: Record<string, string> = {
  survival: 'Выживание', creative: 'Творчество',
  adventure: 'Приключение', spectator: 'Спектатор',
}

const MOTION_LABEL: Record<string, string> = {
  idle: 'Покой', walk: 'Ходьба', run: 'Бег',
  jump: 'Прыжок', fall: 'Падение', fly: 'Полёт',
}

/* ─── Hidden telemetry fields (persisted to localStorage) ─── */
type BlockId = 'time' | 'world' | 'nearby' | 'packets' | 'memory' | 'fps'

const HIDDEN_KEY = 'replay.telemetry.hiddenFields'

const DEFAULT_HIDDEN = ['panels.metrics'] // память и FPS скрыты по умолчанию

const loadHidden = (): Set<string> => {
  try {
    const stored = localStorage.getItem(HIDDEN_KEY)
    if (stored === null) return new Set(DEFAULT_HIDDEN)
    const raw = JSON.parse(stored)
    return new Set(Array.isArray(raw) ? raw as string[] : DEFAULT_HIDDEN)
  } catch { return new Set(DEFAULT_HIDDEN) }
}

const saveHidden = (s: Set<string>) => {
  try { localStorage.setItem(HIDDEN_KEY, JSON.stringify([...s])) } catch {}
}

/* Which fields keep a whole block on screen */
const BLOCK_FIELDS: Record<BlockId, TelemetryFieldId[]> = {
  time: ['time.clock', 'time.tps', 'time.ping', 'time.gamemode'],
  world: ['world.coords', 'world.yaw', 'world.pitch', 'world.chunk', 'world.light', 'world.biome', 'world.speed'],
  nearby: ['panels.nearby'],
  packets: ['panels.packets'],
  memory: ['panels.metrics'],
  fps: ['panels.metrics'],
}

/* Speed sampler.
   bot.entity.velocity stays at 0 during a replay — no physics runs, the server
   only streams positions — so speed is the derivative of the position instead.
   Divided by playback speed to report in-game m/s, not wall-clock m/s. */
let speedSample: { x: number; y: number; z: number; t: number } | null = null
let speedXZ = 0
let speedY = 0
/** Above this a jump is a teleport / seek, not movement (rockets top out ~33 m/s). */
const TELEPORT_SPEED = 100
const SPEED_SMOOTHING = 0.35

const resetSpeed = () => { speedSample = null; speedXZ = 0; speedY = 0 }

function sampleSpeed (pos: { x: number; y: number; z: number } | undefined) {
  if (!pos || packetsReplayState.isSeeking) { resetSpeed(); return }
  const t = performance.now()
  const prev = speedSample
  speedSample = { x: pos.x, y: pos.y, z: pos.z, t }
  if (!prev) return
  const dt = (t - prev.t) / 1000 * (packetsReplayState.speed || 1)
  if (dt <= 0) return
  const xz = Math.hypot(pos.x - prev.x, pos.z - prev.z) / dt
  const y = (pos.y - prev.y) / dt
  if (Math.hypot(xz, y) > TELEPORT_SPEED) return
  speedXZ += (xz - speedXZ) * SPEED_SMOOTHING
  speedY += (y - speedY) * SPEED_SMOOTHING
}

function readTelemetry (): TelemetryData {
  const { bot } = (globalThis as any)
  if (!bot) { resetSpeed(); return EMPTY }
  try {
    const pos = bot.entity?.position
    const x = pos ? Math.round(pos.x) : 0
    const y = pos ? Math.round(pos.y) : 0
    const z = pos ? Math.round(pos.z) : 0
    const yaw = bot.entity?.yaw == null ? 0 : (bot.entity.yaw * (180 / Math.PI))
    const pitch = bot.entity?.pitch == null ? 0 : (bot.entity.pitch * (180 / Math.PI))
    sampleSpeed(pos)
    const motionXZ = speedXZ
    const vy = speedY
    // Thresholds in m/s: sneak ~1.3, walk ~4.3, sprint ~5.6.
    const motionState = bot.entity?.isInWater ? 'fly'
      : !bot.entity?.onGround && Math.abs(vy) > 1.5 ? (vy > 0 ? 'jump' : 'fall')
        : motionXZ > 4.9 ? 'run'
          : motionXZ > 0.6 ? 'walk'
            : 'idle'

    const players: NearbyPlayer[] = []
    if (bot.entities && pos) {
      for (const [, ent] of Object.entries(bot.entities as Record<string, any>)) {
        if (ent.type !== 'player' || ent.username === bot.username) continue
        const dx = ent.position.x - pos.x
        const dz = ent.position.z - pos.z
        const dy = ent.position.y - pos.y
        const dist = Math.round(Math.hypot(dx, dy, dz))
        if (dist < 300) players.push({ name: ent.username ?? '?', distance: dist })
      }
      players.sort((a, b) => a.distance - b.distance)
    }

    let lightLevel: number | null = null
    let biome: string | null = null
    try {
      if (bot.world && pos) {
        const block = bot.blockAt?.(pos)
        if (block) {
          lightLevel = (block.light ?? 0)
          const biomeObj = block.biome
          if (biomeObj) {
            const raw = biomeObj.displayName ?? biomeObj.name ?? null
            biome = typeof raw === 'string' ? prettifyBiome(raw) : null
          }
        }
      }
    } catch {}

    const now = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    const time = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    const date = formatDate(now)

    return {
      time, date,
      tps: null,
      ping: bot._client?.latency ?? null,
      x, y, z,
      yaw: parseFloat(yaw.toFixed(1)),
      pitch: parseFloat(pitch.toFixed(1)),
      chunkX: Math.floor(x / 16),
      chunkZ: Math.floor(z / 16),
      gameMode: bot.game?.gameMode ?? null,
      biome,
      lightLevel,
      motionXZ: parseFloat(motionXZ.toFixed(2)),
      motionY: parseFloat(vy.toFixed(2)),
      motionState,
      nearbyPlayers: players.slice(0, 6),
    }
  } catch {
    return EMPTY
  }
}

function prettifyBiome (raw: string): string {
  const cleaned = raw.replace(/^minecraft:/, '').replaceAll('_', ' ').toLowerCase()
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

const FPS_SAMPLES = 48
const PING_SAMPLES = 32
const MEM_SAMPLES = 48

interface PacketStats { down: number; downKbs: number; up: number; upKbs: number }
const PACKETS_EMPTY: PacketStats = { down: 0, downKbs: 0, up: 0, upKbs: 0 }

export default function TelemetryPanel () {
  // A 300px panel would swallow a phone screen — mobile starts on the tile.
  const [collapsed, setCollapsed] = useState(isMobileLayout)
  const [settingsOpen, setSettingsOpen] = useState(false)
  /** Panel height captured when settings open, so they never resize the panel. */
  const [frozenHeight, setFrozenHeight] = useState<number | null>(null)
  const [hidden, setHidden] = useState<Set<string>>(loadHidden)
  const [data, setData] = useState<TelemetryData>(EMPTY)
  const { isSeeking: seeking } = useSnapshot(packetsReplayState)

  // Mirror for the rest of the HUD, including the mobile default above.
  useEffect(() => {
    packetsReplayState.telemetryCollapsed = collapsed
  }, [collapsed])

  const wrapperRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelWrapRef = useRef<HTMLDivElement>(null)
  const metricsRowRef = useRef<HTMLDivElement>(null)
  const chunksCardRef = useRef<HTMLDivElement>(null)
  const chunksProgress = useChunksProgress()
  useLayoutEffect(() => {
    const metrics = metricsRowRef.current
    const chunksCard = chunksCardRef.current
    const panel = panelRef.current
    const wrap = panelWrapRef.current
    if (!panel || !wrap) return

    let frame = 0

    const update = () => {
      frame = 0
      // The panel is absolutely positioned so the wrap can animate between the
      // expanded size and the tile; feed the wrap its natural height so that
      // transition has two concrete endpoints instead of `auto`.
      wrap.style.setProperty('--panel-h', `${panel.offsetHeight}px`)
      // Cards ride under the wrap, so they follow the collapse animation frame
      // by frame (ResizeObserver fires throughout the transition).
      // Макет: карточки метрик прижаты к панели на 6, chunks отстоит на 12.
      const wrapBottom = wrap.getBoundingClientRect().bottom
      metrics?.style.setProperty('--metrics-y', `${wrapBottom + 6}px`)
      // Loading card sits under the metric cards (or directly under the panel
      // when both metrics are hidden).
      const chunksTop = metrics ? wrapBottom + 6 + metrics.offsetHeight : wrapBottom
      chunksCard?.style.setProperty('--chunks-y', `${chunksTop + 12}px`)
    }

    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(update)
    }

    update()
    const ro = new ResizeObserver(schedule)
    ro.observe(panel)
    ro.observe(wrap)
    if (metrics) ro.observe(metrics)
    if (chunksCard) ro.observe(chunksCard)
    schedule()
    window.addEventListener('resize', schedule)

    return () => {
      if (frame) cancelAnimationFrame(frame)
      ro.disconnect()
      window.removeEventListener('resize', schedule)
    }
  }, [hidden, settingsOpen, !!chunksProgress])
  const [fps, setFps] = useState(0)
  const [fpsHistory, setFpsHistory] = useState<number[]>([])
  const [pingHistory, setPingHistory] = useState<number[]>([])
  const [memMb, setMemMb] = useState(0)
  const [memHistory, setMemHistory] = useState<number[]>([])
  const [packets, setPackets] = useState<PacketStats>(PACKETS_EMPTY)

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const frames = useRef(0)
  const last = useRef(performance.now())
  const raf = useRef(0)

  /* Visibility helpers — a block stays as long as one of its fields is on */
  const has = (id: TelemetryFieldId) => !hidden.has(id)
  const isVisible = (id: BlockId) => BLOCK_FIELDS[id].some(has)
  const applyHidden = (next: Set<string>) => {
    setHidden(next); saveHidden(next)
  }
  const toggleSettings = () => {
    if (!settingsOpen) setFrozenHeight(panelRef.current?.offsetHeight ?? null)
    setSettingsOpen(v => !v)
  }

  useEffect(() => {
    if (!settingsOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSettingsOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [settingsOpen])

  // Telemetry tick (250ms): coords, ping, memory
  useEffect(() => {
    timerRef.current = setInterval(() => {
      const next = readTelemetry()
      setData(next)
      if (next.ping !== null) {
        setPingHistory(h => {
          const n = [...h, next.ping!]
          if (n.length > PING_SAMPLES) n.shift()
          return n
        })
      }
      const mem = (performance as any)?.memory?.usedJSHeapSize ?? 0
      if (mem) {
        const mb = mem / 1024 / 1024
        setMemMb(mb)
        setMemHistory(h => {
          const n = [...h, mb]
          if (n.length > MEM_SAMPLES) n.shift()
          return n
        })
      }
    }, 250)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [])

  // FPS counter (rAF, 500ms bucket)
  useEffect(() => {
    const loop = () => {
      frames.current++
      const now = performance.now()
      if (now - last.current >= 500) {
        const v = Math.round(frames.current * 1000 / (now - last.current))
        frames.current = 0
        last.current = now
        setFps(v)
        setFpsHistory(h => {
          const n = [...h, v]
          if (n.length > FPS_SAMPLES) n.shift()
          return n
        })
      }
      raf.current = requestAnimationFrame(loop)
    }
    raf.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf.current)
  }, [])

  // Packet rate counters (1Hz aggregation)
  useEffect(() => {
    const { bot } = (globalThis as any)
    if (!bot?._client) return
    const acc = { downCount: 0, downBytes: 0, upCount: 0, upBytes: 0 }
    const onPacket = (_data: any, _meta: any, buffer: any) => {
      acc.downCount++
      acc.downBytes += buffer?.length ?? 0
    }
    const onWrite = () => { acc.upCount++ }
    bot._client.on('packet', onPacket)
    bot._client.on('writePacket', onWrite)
    const id = setInterval(() => {
      setPackets({
        down: acc.downCount,
        downKbs: acc.downBytes / 1024,
        up: acc.upCount,
        upKbs: 0,
      })
      acc.downCount = 0; acc.downBytes = 0; acc.upCount = 0; acc.upBytes = 0
    }, 1000)
    return () => {
      try { bot._client.off?.('packet', onPacket) } catch {}
      try { bot._client.off?.('writePacket', onWrite) } catch {}
      clearInterval(id)
    }
  }, [])

  const gmLabel = data.gameMode ? (GAME_MODE_LABEL[data.gameMode] ?? data.gameMode) : null
  const motionTag = MOTION_LABEL[data.motionState]
  const hasTags = (has('world.light') && data.lightLevel !== null)
    || (has('world.biome') && ((motionTag && data.motionState !== 'idle') || !!data.biome))

  /* ─── Content blocks (each independently hide-able in edit mode) ─── */
  const contentBlocks: Array<{ id: BlockId; available: boolean; render: () => ReactNode }> = [
    {
      id: 'time', available: true,
      render: () => (
        <div className={styles.group}>
          <div className={styles.timeRow}>
            {has('time.clock') && (
              <div className={styles.timeMain}>
                <span className={styles.timeVal}>{data.time}</span>
                <span className={styles.timeLbl}>{data.date}</span>
              </div>
            )}
            {has('time.tps') && data.tps !== null && (
              <div className={styles.timeStat}>
                <span className={styles.timeStatVal}>{data.tps.toFixed(1)}</span>
                <span className={styles.timeStatLbl}>TPS</span>
              </div>
            )}
            {has('time.ping') && data.ping !== null && (
              <div className={styles.timeStat}>
                <span className={styles.timeStatVal}>{data.ping}</span>
                <span className={styles.timeStatLbl}>ms</span>
              </div>
            )}
          </div>
          {has('time.gamemode') && gmLabel && (
            <div className={styles.tagRow}>
              <span className={styles.tagPrimary}>{gmLabel}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'world', available: true,
      render: () => (
        <div className={styles.group}>
          {has('world.coords') && (
            <div className={styles.section}>
              <span className={styles.sectionLbl}>Координаты XYZ</span>
              <div className={styles.coords}>
                <span>{data.x}</span>
                <span>{data.y}</span>
                <span>{data.z}</span>
              </div>
            </div>
          )}
          {(has('world.yaw') || has('world.pitch') || has('world.chunk')) && (
            <div className={styles.camRow}>
              {has('world.yaw') && (
                <div className={styles.camItem}>
                  <span className={styles.camLbl}>Yaw</span>
                  <span className={styles.camVal}>{data.yaw}°</span>
                </div>
              )}
              {has('world.pitch') && (
                <div className={styles.camItem}>
                  <span className={styles.camLbl}>Pitch</span>
                  <span className={styles.camVal}>{data.pitch}°</span>
                </div>
              )}
              {has('world.chunk') && (
                <div className={styles.camItem}>
                  <span className={styles.camLbl}>Чанк X, Z</span>
                  <span className={styles.camVal}>{data.chunkX}&nbsp;&nbsp;{data.chunkZ}</span>
                </div>
              )}
            </div>
          )}
          {hasTags && (
            <div className={styles.tagsRow}>
              {has('world.light') && data.lightLevel !== null && (
                <span className={styles.tag}>
                  <LightBulbIcon className={styles.tagIcon} />{data.lightLevel}
                </span>
              )}
              {has('world.biome') && motionTag && data.motionState !== 'idle' && (
                <span className={styles.tag}>{motionTag}</span>
              )}
              {has('world.biome') && data.biome && <span className={styles.tag}>{data.biome}</span>}
            </div>
          )}
          {has('world.speed') && <div className={styles.speedRow}>
            <div className={styles.speedItem}>
              <span className={styles.sectionLbl}>Скорость (X/Z)</span>
              <div className={styles.speedVal}>
                {data.motionXZ.toFixed(2)} <span className={styles.dim}>м/с</span>
              </div>
            </div>
            <div className={styles.speedItem}>
              <span className={styles.sectionLbl}>(Y)</span>
              <div className={styles.speedVal}>
                {Math.abs(data.motionY) >= 0.05 && (
                  <span className={styles.dim}>{data.motionY > 0 ? '↑' : '↓'} </span>
                )}
                {Math.abs(data.motionY).toFixed(2)} <span className={styles.dim}>м/с</span>
              </div>
            </div>
          </div>}
        </div>
      ),
    },
    {
      id: 'nearby', available: true,
      render: () => (
        <div className={styles.section}>
          <span className={styles.sectionLbl}>Игроки поблизости</span>
          {data.nearbyPlayers.length > 0 ? (
            <div className={styles.nearbyGrid}>
              {data.nearbyPlayers.map((p, i) => (
                <div key={i} className={styles.playerRow}>
                  <span className={styles.avatar}><PlayerAvatar name={p.name} size={20} /></span>
                  <span className={styles.playerName}>{p.name}</span>
                  <span className={styles.playerDist}>{p.distance} м</span>
                </div>
              ))}
            </div>
          ) : (
            <span className={styles.emptyHint}>никого рядом</span>
          )}
        </div>
      ),
    },
    {
      id: 'packets', available: true,
      render: () => (
        <div className={styles.section}>
          <span className={styles.sectionLbl}>Пакеты</span>
          <div className={styles.packetsRow}>
            <span className={styles.packetItem}>
              <span className={styles.packetArrow}>↓</span>
              <span className={styles.packetCount}>{formatCount(packets.down)}</span>
              <span className={styles.dim}>({packets.downKbs.toFixed(1)} КБ/с)</span>
            </span>
            <span className={styles.packetItem}>
              <span className={styles.packetArrow}>↑</span>
              <span className={styles.packetCount}>{formatCount(packets.up)}</span>
              <span className={styles.dim}>({packets.upKbs.toFixed(1)} КБ/с)</span>
            </span>
          </div>
        </div>
      ),
    },
  ]

  const visibleContent = contentBlocks.filter(b => b.available && isVisible(b.id))

  /* ─── Metric cards ─── */
  const memVisible = isVisible('memory')
  const fpsVisible = isVisible('fps')
  const metricsCount = (memVisible ? 1 : 0) + (fpsVisible ? 1 : 0)
  const metricsRow = metricsCount > 0 ? (
    <div
      ref={metricsRowRef}
      className={[
        styles.metricsRow,
        metricsCount === 1 && styles.metricsRowSingle,
      ].filter(Boolean).join(' ')}
      data-replay-ui
    >
      {memVisible && (
        <EditableBlock kind="card">
          <MetricCard
            label="память"
            value={memMb < 1024 ? memMb.toFixed(0) : (memMb / 1024).toFixed(1)}
            suffix={memMb < 1024 ? 'МБ' : 'ГБ'}
            color="rgba(255,255,255,0.9)"
            history={memHistory}
            maxFloor={Math.max(64, ...memHistory) * 1.05}
          />
        </EditableBlock>
      )}
      {fpsVisible && (
        <EditableBlock kind="card">
          <MetricCard
            label="fps"
            value={String(fps)}
            color={fps >= 50 ? '#45C95F' : fps >= 30 ? '#d4a84b' : '#e05555'}
            history={fpsHistory}
            maxFloor={60}
          />
        </EditableBlock>
      )}
    </div>
  ) : null

  return (
    <>
      <div
        ref={wrapperRef}
        className={[
          styles.wrapper,
          collapsed && styles.wrapperCollapsed,
        ].filter(Boolean).join(' ')}
        data-replay-ui
      >
        {/* panelWrap keeps CollapseTab as a sibling of the panel so overflow:hidden
         on the panel does not clip the tab that sticks out of the left edge. */}
        <div
          ref={panelWrapRef}
          className={[styles.panelWrap, collapsed && styles.panelWrapCollapsed].filter(Boolean).join(' ')}
        >
          {/* Both surfaces stay mounted and cross-fade: unmounting one would
              make the collapse a jump cut and lose the size transition. */}
          <div
            ref={panelRef}
            aria-hidden={collapsed}
            className={[
              styles.panel,
              collapsed && styles.panelFaded,
            ].filter(Boolean).join(' ')}
            style={settingsOpen && frozenHeight ? { height: frozenHeight } : undefined}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {/* Header — fixed (never editable, always visible) */}
            <div className={styles.header}>
              <div className={styles.title}>
                <TelemetryIcon className={styles.titleIcon} />
                Телеметрия
              </div>
              <div className={styles.headerBtns}>
                <button
                  className={[styles.iconBtn, settingsOpen && styles.iconBtnAccent].filter(Boolean).join(' ')}
                  onClick={toggleSettings}
                  title="Настройки телеметрии"
                >
                  <SlidersIcon />
                </button>
              </div>
            </div>

            {/* Settings take over the panel body — telemetry itself is hidden
                while they are open, so the panel keeps its size. */}
            {settingsOpen ? (
              <div className={styles.settingsInline}>
                <TelemetrySettings embedded hidden={hidden} onChange={applyHidden} />
              </div>
            ) : visibleContent.map((b, i) => (
              <Fragment key={b.id}>
                {i > 0 && <div className={styles.divider} />}
                <EditableBlock>{b.render()}</EditableBlock>
              </Fragment>
            ))}

          </div>

          <button
            type="button"
            aria-hidden={!collapsed}
            tabIndex={collapsed ? undefined : -1}
            title="Развернуть"
            className={[styles.collapsedTile, !collapsed && styles.collapsedTileHidden].filter(Boolean).join(' ')}
            onClick={() => setCollapsed(false)}
          >
            <TelemetryIcon className={styles.collapsedIcon} />
          </button>

          {/* Shared collapse tab — left edge (panel is right-anchored). */}
          <CollapseTab
            edge="left"
            className={styles.collapseTab}
            collapsed={collapsed}
            onToggle={() => setCollapsed(v => !v)}
          />
        </div>
      </div>
      {/* Portalled, so the provider's hide-while-seeking wrapper does not reach it. */}
      {metricsRow && !seeking && typeof document !== 'undefined' && createPortal(metricsRow, document.querySelector('#player-root') ?? document.body)}
      {chunksProgress && typeof document !== 'undefined' && createPortal(
        <ChunksLoadingCard progress={chunksProgress} cardRef={chunksCardRef} />,
        document.querySelector('#player-root') ?? document.body
      )}
    </>
  )
}

/* ─────────────────────────────────────────────────────────── */

interface EditableBlockProps {
  kind?: 'inline' | 'card'
  children: ReactNode
}

function EditableBlock ({ kind = 'inline', children }: EditableBlockProps) {
  const cls = [styles.block, kind === 'card' && styles.blockCard].filter(Boolean).join(' ')
  return <div className={cls}>{children}</div>
}

/* ─────────────────────────────────────────────────────────── */

interface MetricCardProps {
  label: string
  value: string
  color: string
  history: number[]
  maxFloor: number
  suffix?: string
}

function MetricCard ({ label, value, color, history, maxFloor, suffix }: MetricCardProps) {
  const W = 100
  const H = 32
  const max = Math.max(maxFloor, ...history)
  const step = history.length > 1 ? W / (history.length - 1) : W
  const yFor = (v: number) => {
    const ratio = max > 0 ? Math.min(1, v / max) : 0
    return Math.max(1, Math.min(H - 1, (1 - ratio) * (H - 2) + 1))
  }
  const pts = history.map((v, i) => `${i * step},${yFor(v)}`).join(' ')
  const area = history.length > 1 ? `0,${H} ${pts} ${W},${H}` : ''
  return (
    <div className={styles.metricCard}>
      <span className={styles.metricLbl}>{label}</span>
      <span className={styles.metricVal}>
        <span style={{ color }}>{value}</span>
        {suffix && <span className={styles.metricSuffix}>{suffix}</span>}
      </span>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={styles.metricGraph}>
        {history.length > 1 && (
          <>
            <polygon points={area} fill={color} opacity="0.14" />
            <polyline
              points={pts} fill="none" stroke={color}
              strokeWidth="1" strokeLinejoin="round" opacity="0.85"
              vectorEffect="non-scaling-stroke"
            />
          </>
        )}
      </svg>
    </div>
  )
}
